import { _ } from "../../../core/polyglot/polyglot.js";
import { callGetterFor } from "../../../core/helpers/functional-util.js";
import { formatNoDecimal } from "../../../core/helpers/count-formatting-util.js";
import { Widget } from "@foretagsplatsen/widgetjs";
import LiquidityReport from "../../../models/accounting/liquidityReport/LiquidityReport.js";
import LiquidityWeekReport from "../../../models/accounting/liquidityReport/LiquidityWeekReport.js";

/**
 * `LiquidityReportWidget` displays an instance of a
 * `LiquidityReport` using a table with input fields.
 *
 * @param {boolean} [spec.showInvoiceReportLinks=false] - If true, make 'Accounts receivable' and 'Accounts payable' texts into links to the dedicated report documents.
 * @param {boolean} [spec.editingDisabled=false] - If true, make all input fields read-only to prevent unauthorized users to make any modification.
 */
export default class LiquidityReportWidget extends Widget {
	constructor({
		showInvoiceReportLinks = false,
		editingDisabled = false,
	} = {}) {
		super(...arguments);
		this._showInvoiceReportLinks = showInvoiceReportLinks;
		this._editingDisabled = editingDisabled;
	}

	setLiquidityReport(liquidityReport) {
		this._liquidityReport = liquidityReport;
		this.update();
	}

	getLiquidityReport() {
		const data = {};
		const weekInputFields = this.asJQuery().find("input[week-number]");

		weekInputFields.each((_id, inputField) => {
			const weekNumber = inputField.getAttribute("week-number");
			const field = inputField.getAttribute("field");

			data[weekNumber] ||= { weekNumber };
			data[weekNumber][field] = Number(inputField.value) || 0;

			const endOfWeek = inputField.getAttribute("end-of-week");
			data[weekNumber]["endOfWeek"] = endOfWeek;
		});

		const weekReports = Object.keys(data).map((weekNumber) =>
			LiquidityWeekReport.materialize(data[weekNumber]),
		);

		return new LiquidityReport({
			weeks: weekReports,
			liquidity: this._liquidityReport.getLiquidity(),
		});
	}

	renderContentOn(html) {
		if (!this._liquidityReport) {
			return;
		}

		html.div(
			{
				klass: "table-responsive liquidity-report",
			},
			html.table(
				{ klass: "table table-hover table-condensed" },
				this._renderTableHeaderOn.bind(this),
				this._renderTableBodyOn.bind(this),
			),
		);
	}

	_renderTableHeaderOn(html) {
		html.thead(
			html.tr(
				html.th(),
				this._weeksRenderer((weekReport) => (html) => {
					html.th(
						{ klass: "text-right" },
						_("Week {number}", {
							number: weekReport.getWeekNumber(),
						}),
					);
				}),
			),
		);
	}

	_renderTableBodyOn(html) {
		html.tbody(
			this._renderLiquidityRowOn.bind(this),
			this._titleRowRenderer(_("Summary")),
			this._inputFieldRowRenderer(_("Check limit"), "checkLimit"),
			this._inputFieldRowRenderer(
				_("Other adjustments"),
				"otherAdjustments",
			),
			this._renderWeekAbilityOn.bind(this),
			this._renderIncomingPaymentsOn.bind(this),
			this._renderOutgoingPaymentsOn.bind(this),
		);
	}

	_renderLiquidityRowOn(html) {
		html.tr(
			{ klass: "summary-row" },
			this._titleCellRenderer(
				_("Cash and bank, used bank overdraft included"),
			),
			this._weeksRenderer((week, index) => (html) => {
				if (index === 0) {
					html.render(
						this._numberCellRenderer(
							this._liquidityReport.getLiquidity(),
						),
					);
				} else {
					html.td();
				}
			}),
		);
	}

	_renderWeekAbilityOn(html) {
		html.render(
			this._dataSummaryRowRenderer(
				_("Payment ability - start of the week"),
				"startPaymentAbility",
			),
			this._dataRowRenderer(_("Incoming payments"), "incomingPayments"),
			this._dataRowRenderer(_("Outgoing payments"), "outgoingPayments"),
			this._dataSummaryRowRenderer(
				_("Payment ability - end of the week"),
				"endPaymentAbility",
			),
		);
	}

	_renderIncomingPaymentsOn(html) {
		html.render(
			this._titleRowRenderer(_("Specification - incoming payments")),

			this._dataRowRenderer(
				_("{invoice} - not overdue", {
					invoice: this._invoiceLinkOrTextRenderer(
						"accounts-receivable-report",
						_("Accounts receivable"),
					),
				}),
				"incomingAccountReceivablesNotOverdue",
			),
			this._dataRowRenderer(
				_("overdue"),
				"incomingAccountReceivablesOverdue",
			),
			this._dataSummaryRowRenderer(
				_("Accounts receivable total"),
				"incomingAccountReceivablesTotal",
			),

			this._inputFieldRowRenderer(
				_("Writeoffs"),
				"incomingWriteoffs",
				"subsection-starting-row",
			),

			this._inputFieldRowRenderer(
				_("Cash sales"),
				"incomingSalesCash",
				"subsection-starting-row",
			),
			this._inputFieldRowRenderer(_("New loans"), "incomingNewLoans"),
			this._inputFieldRowRenderer(_("VAT"), "incomingVat"),
			this._inputFieldRowRenderer(_("Other"), "incomingOther"),

			this._dataSummaryRowRenderer(
				_("Incoming payments total"),
				"incomingPayments",
			),
		);
	}

	_renderOutgoingPaymentsOn(html) {
		html.render(
			this._titleRowRenderer(_("Specification - outgoing payments")),

			this._dataRowRenderer(
				_("{invoice} - not overdue", {
					invoice: this._invoiceLinkOrTextRenderer(
						"accounts-payable-report",
						_("Accounts payable"),
					),
				}),
				"outgoingAccountPayablesNotOverdue",
			),
			this._dataRowRenderer(
				_("overdue"),
				"outgoingAccountPayablesOverdue",
			),
			this._dataSummaryRowRenderer(
				_("Account payables total"),
				"outgoingAccountPayablesTotal",
			),

			this._inputFieldRowRenderer(
				_("Writeoffs"),
				"outgoingWriteoffs",
				"subsection-starting-row",
			),

			this._inputFieldRowRenderer(
				_("Upcoming payments"),
				"outgoingUpcomingPayments",
				"subsection-starting-row",
			),
			this._inputFieldRowRenderer(
				_("Autogiro payments"),
				"outgoingAutogiroPayments",
			),

			this._inputFieldRowRenderer(
				_("Salaries"),
				"outgoingSalaries",
				"subsection-starting-row",
			),
			this._inputFieldRowRenderer(
				_("Withholding taxes"),
				"outgoingWithholdingTaxes",
			),
			this._inputFieldRowRenderer(
				_("Payroll taxes"),
				"outgoingPayrollTaxes",
			),

			this._inputFieldRowRenderer(
				_("Preliminary taxes"),
				"outgoingPreliminaryTaxes",
				"subsection-starting-row",
			),
			this._inputFieldRowRenderer(_("VAT"), "outgoingVat"),
			this._inputFieldRowRenderer(_("Other"), "outgoingOther"),

			this._dataSummaryRowRenderer(
				_("Outgoing payments total"),
				"outgoingPayments",
			),
		);
	}

	_inputFieldRowRenderer(title, field, cssClass) {
		return (html) => {
			html.tr(
				{ klass: cssClass || "" },
				this._titleCellRenderer(title),
				this._weeksRenderer((weekReport) => (html) => {
					html.td(this._inputFieldRenderer(weekReport, field));
				}),
			);
		};
	}

	_inputFieldRenderer(weekReport, field) {
		return (html) => {
			const weekNumber = weekReport.getWeekNumber();
			const endOfWeek = weekReport.getEndOfWeek();
			const initialValue = this._fieldValue(weekReport, field);

			const inputSpec = {
				klass: "text-right",
				id: `input-${weekNumber}-${field}`,
				"week-number": `${weekNumber}`,
				"end-of-week": `${endOfWeek}`,
				field,
				value: initialValue,
			};

			if (this._editingDisabled) {
				inputSpec.readonly = "readonly";
			}

			const inputField = html.input(inputSpec);

			const jqueryInput = inputField.asJQuery();

			inputField.change(() => {
				const currentValue = Number(jqueryInput.val());
				if (
					currentValue !== initialValue &&
					!jqueryInput.hasClass("input-changed")
				) {
					jqueryInput.addClass("input-changed");
				}
				if (
					currentValue === initialValue &&
					jqueryInput.hasClass("input-changed")
				) {
					jqueryInput.removeClass("input-changed");
				}
			});
		};
	}

	_dataSummaryRowRenderer(title, field) {
		return this._dataRowRenderer(title, field, "summary-row sum");
	}

	_dataRowRenderer(title, field, cssClass) {
		return (html) => {
			html.tr(
				{
					klass: cssClass || "",
				},
				this._titleCellRenderer(title),
				this._weeksRenderer((weekReport) =>
					this._numberCellRenderer(
						this._fieldValue(weekReport, field),
					),
				),
			);
		};
	}

	_titleRowRenderer(title) {
		return (html) =>
			html.tr(
				{
					klass: "title",
				},
				html.th(
					{
						colspan: `${this._weekCount() + 1}`,
					},
					title,
				),
			);
	}

	_titleCellRenderer(title) {
		return (html) => html.th(title);
	}

	_numberCellRenderer(number) {
		return (html) => {
			html.td({ klass: "text-right" }, formatNoDecimal(number));
		};
	}

	_invoiceLinkOrTextRenderer(route, title) {
		return (html) => {
			if (this._showInvoiceReportLinks) {
				html.a({ href: this._linkTo(route) }, title);
			} else {
				html.render(title);
			}
		};
	}

	_weekCount() {
		return this._liquidityReport.weekCount();
	}

	_weeksRenderer(func) {
		return (html) => {
			this._liquidityReport
				.getWeekReports()
				.forEach((week, index) => html.render(func(week, index)));
		};
	}

	_fieldValue(weekReport, field) {
		return callGetterFor(weekReport, field);
	}
}
